import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type PageHeaderConfig = {
  className?: string;
  headerLeftContent?: ReactNode;
  headerRightContent?: ReactNode;
};

type PageHeaderState = {
  className: string;
  headerLeftContent: ReactNode;
  headerRightContent: ReactNode;
};

const defaultState: PageHeaderState = {
  className: '',
  headerLeftContent: null,
  headerRightContent: null,
};

type PageHeaderContextValue = {
  state: PageHeaderState;
  setPageHeader: (config: PageHeaderConfig) => void;
  resetPageHeader: () => void;
};

const PageHeaderContext = createContext<PageHeaderContextValue | null>(null);

export function PageHeaderProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<PageHeaderState>(defaultState);

  const setPageHeader = useCallback((config: PageHeaderConfig) => {
    setState((prev) => ({
      className: config.className ?? prev.className,
      headerLeftContent:
        config.headerLeftContent !== undefined ? config.headerLeftContent : prev.headerLeftContent,
      headerRightContent:
        config.headerRightContent !== undefined
          ? config.headerRightContent
          : prev.headerRightContent,
    }));
  }, []);

  const resetPageHeader = useCallback(() => {
    setState(defaultState);
  }, []);

  const value = useMemo(
    () => ({ state, setPageHeader, resetPageHeader }),
    [state, setPageHeader, resetPageHeader],
  );

  return <PageHeaderContext.Provider value={value}>{children}</PageHeaderContext.Provider>;
}

export function usePageHeader(config: PageHeaderConfig = {}) {
  const context = useContext(PageHeaderContext);
  if (!context) {
    throw new Error('usePageHeader must be used within PageHeaderProvider (Home layout)');
  }

  const { className = '', headerLeftContent = null, headerRightContent = null } = config;

  const { setPageHeader, resetPageHeader } = context;

  useEffect(() => {
    setPageHeader({ className, headerLeftContent, headerRightContent });
    return () => resetPageHeader();
  }, [className, headerLeftContent, headerRightContent, setPageHeader, resetPageHeader]);
}

export function usePageHeaderState() {
  const context = useContext(PageHeaderContext);
  if (!context) {
    throw new Error('usePageHeaderState must be used within PageHeaderProvider');
  }
  return context.state;
}
